import { useTikTokActions } from 'src/pages/TikTok/TikTok.actions';
import React, { useEffect, useRef, useState } from 'react';
import { Textarea } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eChannelType } from '../../../../../../providers/SocketsProvider/Sockets.enums.ts';
import {
  isTikTokChannel,
  isInstagramChannel,
} from '../../../../../../constants/channel.constants';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import useChatActions from '../../../../Chat.actions.ts';
import { ChatEnum } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatService } from '../../../../Chat.service.ts';
import {
  CHANNEL_TEXT_LIMITS,
  tAttachment,
  tChatAttachment,
} from '../../../../Chat.types.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import {
  createHeldBlobUrl,
  releaseBlobUrlHolder,
} from 'src/utils/blobUrlRegistry';
import {
  remainingAttachmentsAfterSend,
  retainDraftBlobAttachments,
  settleDraftAttachmentSend,
} from '../../draftMessages.store';
import {
  attachmentSendAlert,
  sendAttachmentsSequentially,
} from '../../MessageBar.utils.ts';
import { generateVideoPoster } from '../Assets/Assets.functions.ts';
import { isVideo } from '../Thumbnail/Thumbnail.functions.ts';
import { isEmptyFile } from '../ToolActions/components/DriveFilesAction/components/UploadFileAction/UploadFileAction.constants.ts';
import { listVariables } from '../VariablesMenu/VariablesMenu.constants.ts';
import { VariablesMenu } from '../VariablesMenu/VariablesMenu.tsx';
import { tListVariables } from '../VariablesMenu/VariablesMenu.types.ts';
import type { tUseComposerServiceSealResult } from 'src/pages/Wallet/hooks/useComposerServiceSeal';
import { useTopUpRoleGate } from 'src/pages/Wallet/hooks/useTopUpRoleGate';
import { useTopUpModal } from 'src/pages/Wallet/hooks/useTopUpModal';
import ComposerNoBalanceBar from 'src/pages/Wallet/components/ComposerNoBalanceBar/ComposerNoBalanceBar.component';
import ComposerSendSeal from 'src/pages/Wallet/components/ComposerSendSeal/ComposerSendSeal.component';
import './TextAreaContainer.styles.scss';

// Show the character counter only once the composer is close to the cap.
// Instagram and TikTok show it unconditionally (unchanged); WhatsApp's 4096 is
// far enough away that a permanent "12/4096" would be noise on every message.
const COUNTER_REVEAL_RATIO = 0.9;

// RET-2232 D-30 (vuelta 5): estos cuatro campos ya NO se calculan aquí — los
// calcula `useComposerServiceSeal` UNA vez en `MessageBar.content.tsx`, que
// monta este componente en sus TRES sitios de render (:920, :945, :955) y
// además a `<SendAction/>`, su HERMANO.
//
// D-31 (vuelta 6): bajan como props OBLIGATORIAS, no `Partial<>` con
// defaults. La vuelta 5 las dejó opcionales (`seal = null, isBlocked =
// false`) para que los arneses que ensamblan `<TextAreaContainer/>` a mano
// siguieran compilando, y el resultado fue peor que el problema: esos
// arneses no se rompieron, se quedaron CIEGOS — recibían el default,
// dejaban de medir, y los cuatro caminos que cubrían (el clic con la barra
// puesta, el Enter con el selector abierto, la deriva de un hermano que
// limpia `blockTextArea`, el selector de plantillas abierto) perdían su
// lente sin que nada lo dijera. El repo ya tiene la lección escrita en su
// `CLAUDE.md` (Testing): «making the field optional to spare the churn
// permanently weakens the production contract». Con las props obligatorias,
// un arnés que las olvide NO COMPILA, que es ruidoso; con `Partial`, se
// calla. Los tres arneses se re-apuntaron en la misma vuelta (D-32): cada
// uno alimenta el veredicto REAL de `useComposerServiceSeal`, nunca un
// objeto inventado a mano.
type tTextAreaContainerProps = tUseComposerServiceSealResult;

export default function TextAreaContainer({
  seal,
  sealTooltip,
  isBlocked,
  costLabel,
  shortfall,
}: tTextAreaContainerProps) {
  const { addAlert } = useToastContext();
  const {
    blockTextArea,
    textAreaContainerRef,
    showAllowTypesBanner,
    setLoading,
    replyMessage,
    setReplyMessage,
    setBlockTextArea,
    selectedChatLeadId,
    selectedChat,
    setDroppedAttachments,
    chatEvents,
    textAreaRef,
  } = useChatContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const tiktokActions = useTikTokActions(
    currentWorkspaceId,
    selectedChat?.leadId,
    selectedChat?.integration === eChannelType.TIKTOK,
  );
  const { sendTextMessage, sendFileMessage, sendDriveFile } = useChatActions();
  const currentLeadIdRef = useRef(selectedChatLeadId);
  currentLeadIdRef.current = selectedChatLeadId;
  // `errors/errors` holds the copy for an attachment send the server refused.
  const { t } = useTranslation(['chat/chat', 'errors/errors']);
  const {
    messageBarValue,
    setMessageBarValue,
    clearComposerKeepingDraft,
    chatAttachments,
    setChatAttachments,
    selectedAction,
    setSelectedAction,
    draftMessages,
    setDraftMessages,
    isSendingRef,
    quickReplyTriggerRef,
  } = useMessageBarContext();

  const [openVariableMenu, setOpenVariableMenu] = useState<boolean>(false);
  const [dataVariables, setDataVariables] =
    useState<tListVariables[]>(listVariables);

  const textArea = textAreaRef.current;
  const isLeadFromInstagram = isInstagramChannel(
    selectedLeadForChatModal?.chat?.integration,
  );
  const isLeadFromTikTok = isTikTokChannel(
    selectedLeadForChatModal?.chat?.integration,
  );
  // WhatsApp is the fallback channel here, so it inherits the WhatsApp cap.
  const textLimit = isLeadFromInstagram
    ? CHANNEL_TEXT_LIMITS.INSTAGRAM
    : isLeadFromTikTok
      ? CHANNEL_TEXT_LIMITS.TIKTOK
      : CHANNEL_TEXT_LIMITS.WHATSAPP;

  // Typing indicator state (WAPI only)
  const typingFiredRef = useRef(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workspaceIdRef = useRef(currentWorkspaceId);
  const selectedChatRef = useRef(selectedChat);
  workspaceIdRef.current = currentWorkspaceId;
  selectedChatRef.current = selectedChat;

  const isWapiChat = selectedChat?.integration === eChannelType.WHATSAPP_API;

  // RET-2232, D-1/D-2 (reubicado por D-30 en la vuelta 5): un solo sitio
  // calcula el sello + la barra sin saldo del texto libre — cubre las tres
  // ramas de render de `<TextAreaContainer/>` sin triplicar la condición. La
  // lógica entera vive en el hook (slice del wallet); este componente sólo
  // pinta. Lo que cambió con D-30: ese sitio único YA NO es esta llamada —
  // es `useComposerServiceSeal` en `MessageBar.content.tsx`, el padre que
  // monta las tres ramas Y a `<SendAction/>` (su hermano), y que por eso
  // nunca se desmonta mientras exista el compositor. `seal`, `isBlocked`,
  // `costLabel` y `shortfall` llegan como props (ver el tipo del componente,
  // arriba); este archivo no vuelve a importar `useComposerServiceSeal`.
  const { canTopUp: canTopUpBalance } = useTopUpRoleGate();
  const { openOneOffWithAmount, modal: walletTopUpModal } = useTopUpModal();

  // RET-2232, D-23 (vuelta 3): la vuelta 1 suprimía TAMBIÉN la barra cuando
  // `ChatEnum.WABTEMPLATES` estaba abierto (`isWalletBlocked = isBlocked &&
  // !suppressWalletComposerUi`), y ese valor suprimido gobernaba la barra, el
  // textarea Y el envío. Un verificador adversarial independiente demostró,
  // EJECUTANDO la prueba (no argumentando), que eso deja un compositor que
  // PARECE vivo y no lo está: con el selector abierto, `blockTextArea` (la
  // bandera visual) quedaba en `false`, el textarea se pintaba habilitado, y
  // `Enter` llegaba a `sendTextMessage` sin consultar el saldo — el hueco del
  // TECLADO.
  //
  // El arreglo es estructural, no un `if` más adentro de `handleKeyDown`:
  // `isWalletBlocked` desaparece. Un solo veredicto, `isBlocked` (esta prop),
  // gobierna pintar la barra, bloquear el textarea (`blockTextArea`, el
  // efecto más abajo) y bloquear el envío (`isComposerWalletBlocked`, cuyo
  // escritor vive en `MessageBar.content.tsx` desde D-30 — ver ese archivo).
  // Sin `<Textarea/>` montado no hay `handleKeyDown` que disparar — el hueco
  // se cierra por construcción, no por un guardia más que se pueda reabrir
  // por descuido.
  //
  // `suppressWalletComposerUi` sobrevive ÚNICAMENTE como condición de render
  // del `<ComposerSendSeal/>`: con el selector de plantillas abierto esa
  // superficie ya tiene su propio badge, y el chip del sello es lo que de
  // verdad competía con él visualmente. La BARRA, en cambio, ya no se
  // suprime: con el selector abierto se pinta y reemplaza al textarea igual
  // que en cualquier otro estado. Precio aceptado: en el caso raro de
  // selector abierto + intento de envío de plantilla + saldo corto pueden
  // convivir dos barras — cosmético, las dos dicen lo mismo y ofrecen el
  // mismo camino a recargar. Se cambia un defecto de dinero por uno de
  // estética, a propósito.
  const suppressWalletComposerUi = selectedAction === ChatEnum.WABTEMPLATES;

  // D-16 (arrastrado de la vuelta 1) / D-30 (vuelta 5): la barra tiene que
  // bloquear el ENVÍO, no sólo tapar el textarea, y `SendAction` es HERMANO
  // de `TextAreaContainer` en `MessageBar.content.tsx` (no su hijo) — lee
  // `messageBarValue` del CONTEXTO, no del DOM, así que quitar el
  // `<Textarea/>` de aquí no le impide mandar el borrador ya tecleado (o el
  // restaurado desde `draftMessages`). El campo que de verdad se lo impide es
  // `isComposerWalletBlocked`, y D-30 movió su ÚNICO escritor a
  // `MessageBar.content.tsx` — el mismo componente que llama al hook y que
  // nunca se desmonta mientras exista el área del compositor (a diferencia de
  // este, que no se monta en absoluto con el Lead Agent activo). Ese efecto
  // ya no vive aquí; este archivo no vuelve a tocar `isComposerWalletBlocked`.
  //
  // Lo que SÍ sigue aquí es `blockTextArea`/`setBlockTextArea`: la bandera
  // VISUAL del propio `<Textarea/>` de este componente. No hay nada de ese
  // elemento que proteger en una rama que no lo monta (el Lead Agent activo,
  // donde tampoco hay `handleKeyDown` que disparar), y `Chat.hook.ts` ya la
  // resetea en cada cambio de chat (`loadChat`: `true` mientras carga, `false`
  // en su `finally`) sin depender de que este componente esté montado — por
  // eso mover SÓLO este escritor no habría cerrado el hueco del Lead Agent
  // (el guardia de verdad es el otro campo), y por eso basta con que siga
  // sincronizado aquí, para cuando el `<Textarea/>` sí existe.
  //
  // D-24 (vuelta 3), vigente: el guardia vive lo que vive el ÁMBITO
  // (workspace, conversación), no el componente — este efecto suelta
  // `blockTextArea` cuando `isBlocked` (la prop) se vuelve `false`, y no por
  // el desmontaje en sí. `MessageBar.hook.ts` resetea `selectedAction` a
  // `ChatEnum.CHAT` en cuanto cambia `selectedChat?.leadId`, así que un
  // cambio de conversación (o de workspace) siempre saca a
  // `TextAreaContainer` de la rama `WABTEMPLATES_VARIABLES` y lo remonta con
  // el veredicto fresco de la conversación nueva.
  useEffect(() => {
    setBlockTextArea(isBlocked);
  }, [isBlocked, setBlockTextArea]);

  // Reset typing indicator when chat changes
  useEffect(() => {
    typingFiredRef.current = false;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [selectedChat?.id]);

  function triggerTypingIndicator() {
    if (typingFiredRef.current || !isWapiChat || !workspaceIdRef.current)
      return;

    // Find the last inbound message with a providerId
    const lastInbound = [...chatEvents]
      .reverse()
      .find(
        (event) => event.message && !event.message.fromMe && event.providerId,
      );

    if (!lastInbound?.providerId) return;

    const handle = selectedLeadForChatModal?.handle;
    if (!handle) return;

    typingFiredRef.current = true;
    ChatService.sendTypingIndicator(workspaceIdRef.current, {
      messageId: lastInbound.providerId,
      handle,
    });

    // Reset after 25s (Meta's auto-dismiss window)
    typingTimeoutRef.current = setTimeout(() => {
      typingFiredRef.current = false;
      typingTimeoutRef.current = null;
    }, 25000);
  }

  async function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    const nextDraftMessages = draftMessages ? [...draftMessages] : [];
    let currentDraft = -1;

    if (selectedChatLeadId && Array.isArray(draftMessages)) {
      currentDraft = draftMessages.findIndex(
        (draft) => draft.leadId === selectedChatLeadId,
      );
    }

    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !blockTextArea &&
      !showAllowTypesBanner
    ) {
      e.preventDefault();
      if (isSendingRef.current) return;
      isSendingRef.current = true;

      const capturedLeadId = selectedChatLeadId;
      const capturedMessage = textArea?.value ?? '';
      try {
        if (
          selectedAction !== ChatEnum.QUICK_ACTIONS &&
          selectedAction !== ChatEnum.CREATE_QUICK_ACTION
        ) {
          if (textArea) {
            setBlockTextArea(true);

            // Check if message contains @Leadsales AI
            if (textArea.value.includes('@Leadsales AI')) {
              // Clear attachments and reset form
              setChatAttachments([]);
              if (currentDraft > -1) {
                nextDraftMessages.splice(currentDraft, 1);
                setDraftMessages(nextDraftMessages);
              }
              textArea.style.height = '64px';
              setReplyMessage(null);
              return;
            }

            // Normal message sending logic (when @Leadsales AI is not present)
            if (textArea.value.trim() && !chatAttachments.length) {
              const textResult = await sendTextMessage(textArea.value);
              // RET-2349 (R4): a plan-blocked send keeps text, draft, reply.
              if (textResult?.blockedByPlan) return;
            }

            if (chatAttachments.length) {
              const attachmentsResult = await sendAttachmentsSequentially({
                chatAttachments,
                messageBarValue: textArea?.value || '',
                sendFileMessage,
                sendDriveFile,
                callerMethod: 'TextAreaContainer.handleKeyDown',
              });
              const stillOnOriginalLead =
                currentLeadIdRef.current === capturedLeadId;
              if (stillOnOriginalLead) {
                setChatAttachments((current) =>
                  remainingAttachmentsAfterSend(current, {
                    attempted: chatAttachments,
                    failed: attachmentsResult.failedAttachments,
                  }),
                );
              }
              const attachmentsAlert = attachmentSendAlert({
                result: attachmentsResult,
                t,
              });
              if (attachmentsAlert) {
                addAlert(attachmentsAlert);
              }
              // RET-2349 (R4): nothing was sent; keep text, draft, reply.
              if (attachmentsResult.blockedByPlan) return;
              setDraftMessages((current) =>
                settleDraftAttachmentSend(current, {
                  leadId: capturedLeadId,
                  message: capturedMessage,
                  attempted: chatAttachments,
                  failed: attachmentsResult.failedAttachments,
                }),
              );
              if (!stillOnOriginalLead) return;
            }

            // BUG-1134: sin condicionar a `currentDraft`, que se calculó antes
            // del envío y puede estar desfasado. `settleDraftAttachmentSend` ya
            // resuelve bien el caso "este lead no tiene borrador".
            setDraftMessages((current) =>
              settleDraftAttachmentSend(current, {
                leadId: capturedLeadId,
                message: capturedMessage,
                attempted: [],
                failed: [],
              }),
            );

            setMessageBarValue('');
            textArea.style.height = '64px';
            setReplyMessage(null);

            // Reset typing indicator so it fires again for the next message
            typingFiredRef.current = false;
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = null;
            }
          }
        }
      } catch (err) {
        LLog.error(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'TextAreaContainer.component',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Error handleKeyDown triggered text area',
          err as unknown as Error,
        );
        // BUG-1134: vacía la caja pero NO el borrador, igual que en SendAction.
        clearComposerKeepingDraft();
        setReplyMessage(null);
      } finally {
        isSendingRef.current = false;
        if (selectedChatLeadId === capturedLeadId) {
          setBlockTextArea(false);
        }
      }
    }

    if (e.key === '/' && !blockTextArea) {
      // BUG-1157: la `/` cae donde está el cursor; se guarda para que el panel
      // busque desde esa `/` y no desde la primera del texto.
      quickReplyTriggerRef.current = textAreaRef.current?.selectionStart ?? null;
      setSelectedAction(ChatEnum.QUICK_ACTIONS);
    } else if (
      selectedAction === ChatEnum.QUICK_ACTIONS &&
      textAreaRef?.current &&
      textAreaRef.current.value === ''
    ) {
      setSelectedAction(ChatEnum.CHAT);
    }

    // Shortcut for @Leadsales AI
    if (e.key === 'a' && !blockTextArea) {
      const textArea = textAreaRef.current;
      if (textArea) {
        const start = textArea.selectionStart;
        const currentValue = textArea.value;

        // Check if the last 3 characters before cursor are "@le"
        const beforeCursor = currentValue.substring(0, start);
        if (beforeCursor.endsWith('@le')) {
          e.preventDefault();

          const end = textArea.selectionEnd;
          const afterCursor = currentValue.substring(end);

          // Replace "@le" + "a" with "@Leadsales AI "
          const beforeShortcut = beforeCursor.substring(
            0,
            beforeCursor.length - 3,
          );
          const insertText = '@Leadsales AI ';
          const newValue = `${beforeShortcut}${insertText}${afterCursor}`;
          setMessageBarValue(newValue);

          // Set cursor position after the inserted text
          const cursorPosition = beforeShortcut.length + insertText.length;
          setTimeout(() => {
            textArea.setSelectionRange(cursorPosition, cursorPosition);
            textArea.focus();
          }, 0);
        }
      }
    }
  }

  function handleKeyUp(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // BUG-1134: aquí se escribía el borrador. Ya no: `setMessageBarValue` lo
    // sincroniza en `MessageBar.hook`, así que el borrador refleja el composer
    // venga el texto de una tecla o de un emoji, una respuesta rápida o una
    // sugerencia de IA. Persistirlo desde el teclado era justamente lo que lo
    // dejaba desactualizado frente a esas vías.
    const { key, currentTarget } = e;
    const query = currentTarget.value.toLowerCase();

    switch (key) {
      case '{':
        setOpenVariableMenu(true);
        break;
      case '}':
        setOpenVariableMenu(false);
        break;
      default:
        if (query.length > 0) {
          const filtered = dataVariables.filter((variable) =>
            variable.name.toLowerCase().includes(key),
          );
          setDataVariables(filtered.length ? filtered : listVariables);
        } else {
          setDataVariables(listVariables);
          setOpenVariableMenu(false);
        }
        break;
    }
  }

  function addVariableToTextArea(item: string) {
    setOpenVariableMenu(false);
    if (textAreaRef.current) {
      const textArea = textAreaRef.current;
      // Get the current cursor position
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;

      // Get the current text value of the textarea
      const currentValue = textArea.value;

      // We split the text into two parts: before and after the cursor
      const beforeCursor = currentValue.substring(0, start);
      const afterCursor = currentValue.substring(end);

      // Insert the text between the two parts
      const newValue = `${beforeCursor}${item}}${afterCursor}`;
      setMessageBarValue(newValue);

      // Finally we keep the cursor after the inserted text
      const cursorPosition = start + `{${item}}`.length;
      textArea.setSelectionRange(cursorPosition, cursorPosition);
    }
  }

  function handleTextAreaHeight(textArea: HTMLTextAreaElement) {
    textArea.style.height = 'auto';
    textArea.style.height = `${textArea.scrollHeight}px`;
  }

  // Auto-focus textarea when opening/switching chats or after sending a message
  useEffect(() => {
    if (blockTextArea) return;
    const textArea = textAreaRef.current;
    if (!textArea) return;

    // Only auto-focus if no other input/textarea is currently focused
    const active = document.activeElement;
    const isInputFocused =
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement;
    if (!isInputFocused) {
      textArea.focus();
    }
  }, [selectedChatLeadId, blockTextArea]);

  // Height auto-resize
  useEffect(() => {
    const textArea = textAreaRef.current;
    if (!textArea) return;

    const handler = () => handleTextAreaHeight(textArea);
    textArea.addEventListener('input', handler);

    return () => {
      textArea.removeEventListener('input', handler);
    };
  }, []);

  // Handle Leadsales AI action selection
  useEffect(() => {
    if (selectedAction === ChatEnum.LEADSALES_AI) {
      const textArea = textAreaRef.current;
      if (textArea) {
        const start = textArea.selectionStart;
        const end = textArea.selectionEnd;
        const currentValue = textArea.value;

        // Split the text into before and after cursor
        const beforeCursor = currentValue.substring(0, start);
        const afterCursor = currentValue.substring(end);

        // Insert "@Leadsales AI " (with space) at cursor position
        const insertText = '@Leadsales AI ';
        const newValue = `${beforeCursor}${insertText}${afterCursor}`;
        setMessageBarValue(newValue);

        // Set cursor position after the inserted text
        const cursorPosition = start + insertText.length;
        setTimeout(() => {
          textArea.setSelectionRange(cursorPosition, cursorPosition);
          textArea.focus();
        }, 0);

        // Reset action to CHAT
        setSelectedAction(ChatEnum.CHAT);
      }
    }
    // BUG-1134: `setMessageBarValue` NO va en las dependencias. Dejó de ser un
    // `setState` de identidad estable, así que listarlo vuelve a correr este
    // efecto en cada cambio de lead; y como el reset a `CHAT` solo ocurre
    // dentro del `if (textArea)`, una acción trabada en `LEADSALES_AI` acabaría
    // inyectando «@Leadsales AI » en el composer del lead SIGUIENTE.
  }, [selectedAction, setSelectedAction]);

  useEffect(() => {
    if (replyMessage) {
      setTimeout(() => textAreaRef.current?.focus(), 0);
    }
  }, [replyMessage]);

  async function handleOnPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (isSendingRef.current) return;
    // Captured before the first await. selectedChatRef is refreshed every render,
    // so comparing against it afterwards tells us whether the agent moved on.
    const originLeadId = selectedChat?.leadId;
    const uploadHolder = {};
    try {
      setBlockTextArea(true);
      setLoading(true);

      const items = e.clipboardData.files;

      for (const file of items) {
        if (isEmptyFile(file)) {
          addAlert({
            type: eAlertType.WARNING,
            message: t('driveFilesAction.emptyFile', { fileName: file.name }),
          });
          continue;
        }

        const response = await sendFileMessage(file, false, true);

        const newAttachment = response as tAttachment;

        let poster = '';

        if (isVideo(file.type)) {
          poster = await generateVideoPoster(file);
        }
        const source =
          newAttachment.url || createHeldBlobUrl(file, uploadHolder);

        const attachment: tChatAttachment = {
          // A pasted attachment with a remote URL re-sends via
          // sendDriveFile — use the transcoded JPEG's metadata, not the original
          // HEIC's, or the content (JPEG) and label (image/heic) would drift and
          // ImageContent would wrongly show the "unsupported" fallback (BUG-712).
          // The same holds for WAPI conversions (a 16-bit PNG → JPEG, BUG-880).
          type: newAttachment.mimeType ?? file.type,
          src: source,
          name: newAttachment.filename ?? file.name,
          size: file.size,
          poster,
          leadId: originLeadId,
          ...(newAttachment.url ? {} : { file }),
        };
        retainDraftBlobAttachments(originLeadId, [attachment]);

        if (selectedChatRef.current?.leadId === originLeadId) {
          setChatAttachments((prevAttachments) => [
            ...prevAttachments,
            attachment,
          ]);
        } else {
          // The agent switched leads while this uploaded. Park it rather than
          // drop it into whoever is on screen now — MessageBar's merge effect
          // hands it back when its own lead is reopened.
          setDroppedAttachments((prevDropped) => [...prevDropped, attachment]);
        }
      }
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al pegar tus archivos. Intenta mas tarde.',
      });
    } finally {
      releaseBlobUrlHolder(uploadHolder);
      // blockTextArea is composer-global, not per-lead, so it must always be
      // released; holding it after a lead switch would wedge the new lead's
      // composer. (The check that used to guard this compared a closure variable
      // with itself and was always true, so this preserves the real behavior.)
      setBlockTextArea(false);
      setLoading(false);
    }
  }

  return (
    <div className="TextAreaContainer relative" ref={textAreaContainerRef}>
      {openVariableMenu && (
        <VariablesMenu
          list={dataVariables}
          onHandleClick={addVariableToTextArea}
        />
      )}
      {isBlocked ? (
        // RET-2232, D-9/D-11/D-23: la barra REEMPLAZA el compositor — no se
        // afirma "Con costo" (el sello) sobre un envío que no puede salir, y
        // no hay dónde teclear un mensaje bloqueado. D-23: esto YA NO se
        // suprime con `WABTEMPLATES` abierto — sólo el sello se calla (ver
        // `suppressWalletComposerUi` más arriba).
        <ComposerNoBalanceBar
          costLabel={costLabel}
          variant="message"
          recipientName={selectedLeadForChatModal?.name}
          onTopUp={
            canTopUpBalance && shortfall
              ? () => openOneOffWithAmount(shortfall)
              : undefined
          }
        />
      ) : (
        <>
          {/*
            El sello va ANTES del campo y no después, como en el prototipo
            (`.conv-input`: el chip con `flex:none` primero, el texto con
            `flex:1; min-width:0` después). Puesto al final quedaba
            comprimido entre el textarea y la barra de herramientas.
          */}
          {seal && !suppressWalletComposerUi && (
            <ComposerSendSeal seal={seal} tooltip={sealTooltip} />
          )}
          <Textarea
            disabled={blockTextArea || showAllowTypesBanner}
            ref={textAreaRef}
            aria-multiline={true}
            className="TextAreaContainer__TextArea ph-no-capture"
            placeholder={t('textAreaComponentPlaceholder')}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            value={messageBarValue}
            onChange={(e) => {
              setMessageBarValue(e.target.value);
              triggerTypingIndicator();
              if (
                selectedChat?.integration === eChannelType.TIKTOK &&
                e.target.value.trim()
              )
                tiktokActions.typing();
            }}
            maxLength={textLimit}
            onPaste={handleOnPaste}
          />
          {isLeadFromInstagram ||
          isLeadFromTikTok ||
          messageBarValue.length >= textLimit * COUNTER_REVEAL_RATIO ? (
            <p className="mr-2 mt-2 text-sm">{`${messageBarValue.length}/${textLimit}`}</p>
          ) : null}
        </>
      )}
      {walletTopUpModal}
    </div>
  );
}
